import React, { useState, useRef, useEffect } from "react";
import "./Tooltip.css";

interface TooltipProps {
  text?: string;
  position?: "left" | "right";
  error?: boolean;
  children?: React.ReactNode;
  size?: 'default' | 'small';
  delayMs?: number;
  content?: React.ReactNode;
  persistentOnClick?: boolean;
}

const Tooltip: React.FC<TooltipProps> = ({
  text,
  position = "right",
  error = false,
  children,
  size = 'default',
  delayMs = 0,
  content,
  persistentOnClick = false
}) => {
  const [visible, setVisible] = useState(false);
  const [pinned, setPinned] = useState(false);
  const showTimerRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    return () => {
      if (showTimerRef.current !== null) {
        clearTimeout(showTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!persistentOnClick || !pinned) {
      return;
    }
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setPinned(false);
        setVisible(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [pinned, persistentOnClick]);

  const handleMouseEnter = () => {
    if (persistentOnClick && pinned) return;
    if (showTimerRef.current !== null) {
      clearTimeout(showTimerRef.current);
    }
    showTimerRef.current = window.setTimeout(() => {
      setVisible(true);
    }, delayMs);
  };

  const handleMouseLeave = () => {
    if (persistentOnClick && pinned) return;
    if (showTimerRef.current !== null) {
      clearTimeout(showTimerRef.current);
    }
    setVisible(false);
  };

  const handleTogglePinned = (event: React.MouseEvent) => {
    if (!persistentOnClick) {
      return;
    }
    const boxNode = containerRef.current?.querySelector(".tooltip-box");
    if (boxNode && boxNode.contains(event.target as Node)) {
      return;
    }
    if (showTimerRef.current !== null) {
      clearTimeout(showTimerRef.current);
    }
    setPinned((prev) => {
      const next = !prev;
      setVisible(next ? true : false);
      return next;
    });
  };

  return (
    <div
      className="tooltip-container"
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleTogglePinned}
    >
      {children || <img src="/assets/info.svg" alt="Info" className="tooltip-icon" />}
      {visible && (content || text) && (
        <div className={`tooltip-position-wrapper ${position}`}>
          <div className={`tooltip-box ${error ? 'error' : ''} ${size === 'small' ? 'tooltip-box--small' : ''}`}>
            <div className="tooltip-arrow-border"></div>
            <div className="tooltip-arrow"></div>
            {content ?? text}
          </div>
        </div>
      )}
    </div>
  );
};

export default Tooltip;
